/**
 * DefaultAvatar - Renders initials on admin-controlled background
 * when user has no uploaded profile photo.
 * Pulls config from /api/canonical/ui-assets/default_profile_pic
 */

import { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Cache the config so we don't refetch every render
let cachedConfig = null;

export function DefaultAvatar({ firstName, lastName, size = 48, className = "" }) {
  const [config, setConfig] = useState(cachedConfig || {
    background_color: "#000000",
    text_color: "#FFFFFF",
    font_family: "Manrope, sans-serif",
    font_weight: "600"
  });

  useEffect(() => {
    if (cachedConfig) return;
    fetch(`${API}/canonical/ui-assets/default_profile_pic`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.config) {
          cachedConfig = data.config;
          setConfig(data.config);
        } else {
          // Cache the local fallback so we don't refetch and spam 404s
          cachedConfig = config;
        }
      })
      .catch(() => { cachedConfig = config; });
  }, []);

  const initials = `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
  const fontSize = size * 0.38;

  return (
    <div
      className={`flex items-center justify-center rounded-full select-none ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: config.background_color,
        color: config.text_color,
        fontFamily: config.font_family,
        fontWeight: config.font_weight,
        fontSize: `${fontSize}px`,
        lineHeight: 1
      }}
      data-testid="default-avatar"
    >
      {initials || '?'}
    </div>
  );
}

export function DefaultOrgIcon({ orgName, size = 48, className = "" }) {
  const [config, setConfig] = useState({
    background_color: "#000000",
    text_color: "#FFFFFF",
    font_family: "Manrope, sans-serif",
    font_weight: "700",
    border_radius: "12px"
  });

  useEffect(() => {
    fetch(`${API}/canonical/ui-assets/default_org_icon`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data?.config) setConfig(data.config);
      })
      .catch(() => {});
  }, []);

  const initial = (orgName || '')[0]?.toUpperCase() || '?';
  const fontSize = size * 0.42;

  return (
    <div
      className={`flex items-center justify-center select-none ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: config.background_color,
        color: config.text_color,
        fontFamily: config.font_family,
        fontWeight: config.font_weight,
        fontSize: `${fontSize}px`,
        borderRadius: config.border_radius,
        lineHeight: 1
      }}
      data-testid="default-org-icon"
    >
      {initial}
    </div>
  );
}

export function useHostingCoverConfig() {
  const [config, setConfig] = useState({
    background_color: "#1A1A1A",
    gradient: null,
    overlay_opacity: 0.1
  });

  useEffect(() => {
    fetch(`${API}/canonical/ui-assets/default_hosting_cover`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data?.config) setConfig(data.config);
      })
      .catch(() => {});
  }, []);

  return config;
}
